import { useLocation, useNavigate } from "@solidjs/router";
import {
	createEffect,
	createMemo,
	createSignal,
	Match,
	Show,
	Switch,
	untrack,
} from "solid-js";
import type {
	AnswerType,
	Capability,
	Product,
	Question,
	Topic,
	TreeQuestion,
	TreeTopic,
} from "../../api/domain.ts";
import { answerOptions } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { DeleteButton } from "../../components/DeleteButton.tsx";
import {
	Actions,
	Button,
	FieldGroup,
	Form,
	IconButton,
	NumberField,
	SelectField,
	TextField,
} from "../../components/Form.tsx";
import {
	CompareIcon,
	FoldIcon,
	PencilIcon,
	UnfoldIcon,
} from "../../components/icons.tsx";
import { MenuButton } from "../../components/MenuButton.tsx";
import { Modal } from "../../components/Modal.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { TopicTree } from "../../components/TopicTree.tsx";
import { type ChosenNode, TreeChoose } from "../../components/TreeChoose.tsx";
import {
	type PickableQuestion,
	TreeSelect,
} from "../../components/TreeSelect.tsx";
import { useFolding } from "../../components/tree-shell.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import {
	type Anchor,
	type AnchorKind,
	anchorHash,
	anchorId,
	anchorTopic,
	parseAnchor,
	scrollToAnchor,
} from "../../lib/anchor.ts";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { searchCatalog } from "../../lib/catalog-search.ts";
import styles from "./admin.module.scss";
import { QuestionAnswers } from "./QuestionAnswers.tsx";

/**
 * What the dialog over the tree is currently pointed at. Not every state edits:
 * `answers` only reads, but it opens the same dialog, so it belongs in the same
 * state rather than in a second signal that could disagree with this one.
 */
type Editing =
	| { kind: "none" }
	| { kind: "topic"; topic: Topic; fresh: boolean }
	| { kind: "capability"; capability: Capability; fresh: boolean }
	| { kind: "question"; question: QuestionDraft; fresh: boolean }
	| { kind: "answers"; question: TreeQuestion };

/**
 * A question plus the topic it belongs to. Membership lives on the topic rather
 * than the question, so the editor has to carry it alongside and the page has
 * to write both.
 */
type QuestionDraft = { question: Question; topic: string };

/** The collections this screen writes to, named the way the API paths are. */
type Collection = "topics" | "capabilities" | "questions";

/** The tree row a written entity shows up as, which is what addresses it. */
const anchorKinds: Record<Collection, AnchorKind> = {
	topics: "topic",
	capabilities: "capability",
	questions: "question",
};

/** Hands a finished draft back to the page, which is what talks to the API. */
type Write<T> = (entity: T) => void;

const blankTopic = (): Topic => ({
	id: "",
	title: "",
	description: null,
	questions: [],
	relevancy: 0.5,
});

const blankCapability = (): Capability => ({
	id: "",
	label: "",
	description: null,
	topic: "",
	questions: [],
});

const blankQuestion = (): Question => ({
	id: "",
	title: "",
	body: null,
	answer_type: { kind: "rating" },
});

/** Options as one `slug | label | value` per line, which stays readable and diffable. */
const parseOptions = (raw: string) =>
	raw
		.split("\n")
		.map((line) => line.split("|").map((part) => part.trim()))
		.filter((parts) => parts.length >= 2 && parts[0] !== "")
		.map((parts) => ({
			slug: parts[0] ?? "",
			label: parts[1] ?? "",
			value: Number(parts[2] ?? "0"),
		}));

const printOptions = (question: Question): string =>
	answerOptions(question)
		.map((option) => `${option.slug} | ${option.label} | ${option.value}`)
		.join("\n");

const answerTypeOf = (kind: string, raw: string): AnswerType => {
	if (kind === "select") return { kind: "select", options: parseOptions(raw) };
	if (kind === "multi_select") {
		return {
			kind: "multi_select",
			aggregation: "max",
			options: parseOptions(raw),
		};
	}
	if (kind === "bool") return { kind: "bool" };
	if (kind === "text") return { kind: "text" };
	return { kind: "rating" };
};

type EditorProps<T> = {
	/** The entity as it stands. Each editor keeps its own draft of it. */
	initial: T;
	problem?: string;
	onSave: Write<T>;
	onCancel: () => void;
	/** The catalog, so a field that names part of it can offer the tree instead of an id. */
	catalog: TreeTopic[];
	/**
	 * Questions no topic claims. The tree is built from topic membership, so
	 * without these a question that fell out of it could never be picked back in.
	 */
	loose: PickableQuestion[];
};

/** Wraps a single-topic pick in the shape TreeChoose speaks. */
const topicNodes = (id: string | null): ChosenNode[] =>
	id === null || id === "" ? [] : [{ kind: "topic", id }];

const firstTopic = (chosen: ChosenNode[]): string | null =>
	chosen[0]?.id ?? null;

const TopicEditor = (props: EditorProps<Topic>) => {
	const i18n = useI18n();
	const [draft, setDraft] = createSignal<Topic>(props.initial);

	return (
		<Form>
			<TextField
				label={i18n.t().common.title}
				value={draft().title}
				onInput={(title) => setDraft({ ...draft(), title })}
			/>
			<TextField
				label={i18n.t().common.description}
				optional
				multiline
				value={draft().description ?? ""}
				onInput={(description) =>
					setDraft({
						...draft(),
						description: description === "" ? null : description,
					})
				}
			/>
			<NumberField
				label={i18n.t().catalog.relevancy}
				min={0}
				max={1}
				value={draft().relevancy}
				onInput={(relevancy) => setDraft({ ...draft(), relevancy })}
			/>
			<Show when={props.problem !== undefined}>
				<p class={styles.problem}>{props.problem}</p>
			</Show>
			<Actions>
				<Button
					label={i18n.t().common.save}
					tone="primary"
					onClick={() => props.onSave(draft())}
				/>
				<Button label={i18n.t().common.cancel} onClick={props.onCancel} />
			</Actions>
		</Form>
	);
};

const CapabilityEditor = (props: EditorProps<Capability>) => {
	const i18n = useI18n();
	const [draft, setDraft] = createSignal<Capability>(props.initial);

	return (
		<Form>
			<TextField
				label={i18n.t().common.label}
				value={draft().label}
				onInput={(label) => setDraft({ ...draft(), label })}
			/>
			{/*
        The capability is the unit of interpretation, so this text is what a
        recipient rating it or a customer reading its score has to go on.
      */}
			<TextField
				label={i18n.t().common.description}
				hint={i18n.t().catalog.capabilityDescriptionHint}
				optional
				multiline
				value={draft().description ?? ""}
				onInput={(description) =>
					setDraft({
						...draft(),
						description: description === "" ? null : description,
					})
				}
			/>
			<FieldGroup
				label={i18n.t().common.topic}
				hint={i18n.t().catalog.owningTopicHint}
				required
				problem={
					draft().topic === ""
						? i18n.t().catalog.capabilityTopicRequired
						: undefined
				}
			>
				<TreeChoose
					topics={props.catalog}
					kinds={["topic"]}
					single
					clearLabel={i18n.t().common.none}
					chosen={topicNodes(draft().topic)}
					onChange={(chosen) =>
						setDraft({ ...draft(), topic: firstTopic(chosen) ?? "" })
					}
				/>
			</FieldGroup>
			<FieldGroup
				label={i18n.t().catalog.linkedQuestions}
				hint={i18n.t().catalog.linkedQuestionsHint}
			>
				{/*
          Ticking a topic here fills in that subtree's questions as they stand
          rather than storing the topic. A capability's question list is what its
          score is derived from, so it must not widen on its own once a report
          has been locked against it.
        */}
				<TreeSelect
					topics={props.catalog}
					allLabel={i18n.t().tree.selectAll}
					loose={props.loose}
					looseLabel={i18n.t().tree.unassigned}
					selected={draft().questions}
					onChange={(questions) => setDraft({ ...draft(), questions })}
				/>
			</FieldGroup>
			<Show when={props.problem !== undefined}>
				<p class={styles.problem}>{props.problem}</p>
			</Show>
			<Actions>
				{/*
          A capability with no topic is invisible: it appears in the catalog only
          under the topic it names, and scoring reaches its questions only
          through it. Refusing the save is cheaper than finding it again later.
        */}
				<Button
					label={i18n.t().common.save}
					tone="primary"
					disabled={draft().topic === ""}
					onClick={() => props.onSave(draft())}
				/>
				<Button label={i18n.t().common.cancel} onClick={props.onCancel} />
			</Actions>
		</Form>
	);
};

const QuestionEditor = (props: EditorProps<QuestionDraft>) => {
	const i18n = useI18n();
	const [draft, setDraft] = createSignal<Question>(props.initial.question);
	const [topic, setTopic] = createSignal<string>(props.initial.topic);
	const [kind, setKind] = createSignal<string>(
		props.initial.question.answer_type.kind,
	);
	const [options, setOptions] = createSignal(
		printOptions(props.initial.question),
	);

	const withType = (): QuestionDraft => ({
		question: { ...draft(), answer_type: answerTypeOf(kind(), options()) },
		topic: topic(),
	});

	return (
		<Form>
			<TextField
				label={i18n.t().common.title}
				value={draft().title}
				onInput={(title) => setDraft({ ...draft(), title })}
			/>
			<TextField
				label={i18n.t().common.description}
				optional
				multiline
				value={draft().body ?? ""}
				onInput={(body) =>
					setDraft({ ...draft(), body: body === "" ? null : body })
				}
			/>
			<FieldGroup
				label={i18n.t().common.topic}
				hint={i18n.t().catalog.questionTopicHint}
			>
				{/*
          A question is only reachable through the topic that lists it, so one
          created without a topic would not appear in the tree at all. The editor
          therefore insists on one rather than letting it be dropped.
        */}
				<TreeChoose
					topics={props.catalog}
					kinds={["topic"]}
					single
					clearLabel={i18n.t().common.none}
					chosen={topicNodes(topic())}
					onChange={(chosen) => setTopic(firstTopic(chosen) ?? "")}
				/>
				<Show when={topic() === ""}>
					<p class={styles.problem}>{i18n.t().catalog.questionTopicRequired}</p>
				</Show>
			</FieldGroup>
			<SelectField
				label={i18n.t().catalog.answerType}
				choices={[
					{ id: "rating", label: "rating" },
					{ id: "bool", label: "bool" },
					{ id: "select", label: "select" },
					{ id: "multi_select", label: "multi_select" },
					{ id: "text", label: "text" },
				]}
				value={kind()}
				onPick={setKind}
			/>
			<Show when={kind() === "select" || kind() === "multi_select"}>
				<TextField
					label={i18n.t().catalog.questions}
					hint="slug | label | 0-5"
					multiline
					value={options()}
					onInput={setOptions}
				/>
			</Show>
			<Show when={kind() === "text"}>
				<p class={styles.note}>{i18n.t().catalog.informational}</p>
			</Show>
			<Show when={props.problem !== undefined}>
				<p class={styles.problem}>{props.problem}</p>
			</Show>
			<Actions>
				<Button
					label={i18n.t().common.save}
					tone="primary"
					disabled={topic() === ""}
					onClick={() => props.onSave(withType())}
				/>
				<Button label={i18n.t().common.cancel} onClick={props.onCancel} />
			</Actions>
		</Form>
	);
};

/** The topic tree with its entities editable in a dialog over it. */
export const CatalogPage = () => {
	const api = useApi();
	const i18n = useI18n();
	const location = useLocation();
	const navigate = useNavigate();

	/**
	 * Which view answer coverage is judged against. It changes what the tree says
	 * about every question, so it drives the tree read rather than sitting beside it.
	 */
	const [against, setAgainst] = createSignal("");
	const tree = createLoader(() =>
		api.tree({
			lang: i18n.locale(),
			...(against() === "" ? {} : { view: against() }),
		}),
	);
	const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
	const topics = createLoader(() =>
		api.list("topics", { lang: i18n.locale() }),
	);
	const questions = createLoader(() =>
		api.list("questions", { lang: i18n.locale() }),
	);
	const capabilities = createLoader(() =>
		api.list("capabilities", { lang: i18n.locale() }),
	);
	const products = createLoader(() =>
		api.list("products", { lang: i18n.locale() }),
	);
	const [editing, setEditing] = createSignal<Editing>({ kind: "none" });
	const [problem, setProblem] = createSignal<string | undefined>(undefined);

	/** Opening an editor always clears the last attempt's complaint. */
	const open = (next: Editing) => {
		setProblem(undefined);
		setEditing(next);
	};

	const close = () => open({ kind: "none" });

	/**
	 * Awaitable, because a caller that wants to touch what the new data renders
	 * has to wait for it to be rendered.
	 */
	const reloadAll = async () => {
		await Promise.all([
			tree.reload(),
			topics.reload(),
			questions.reload(),
			capabilities.reload(),
		]);
	};

	/**
	 * Questions no topic lists. They exist because membership is stored on the
	 * topic, so anything written outside this screen, or written here before the
	 * topic was required, sits outside the tree and would otherwise be invisible.
	 *
	 * Judged against every stored topic rather than the rendered tree: the view
	 * above narrows what the tree returns, and a question hidden by a filter is
	 * not a question without a home.
	 */
	const loose = createMemo<PickableQuestion[]>(() => {
		const placed = new Set(
			(loadedValue(topics.value()) ?? []).flatMap((topic) => topic.questions),
		);
		return (loadedValue(questions.value()) ?? [])
			.filter((question) => !placed.has(question.id))
			.map((question) => ({
				id: question.id,
				title: question.title,
				informational: false,
			}));
	});

	/**
	 * Capabilities whose topic does not exist. A capability names its owning topic
	 * and the tree emits it only under that topic, so one naming nothing, or a
	 * topic since deleted or mistyped, drops out of the catalog without a word.
	 * That matters more than a missing question: scoring reaches a question only
	 * through a capability, so an invisible capability takes its questions' only
	 * route into a report with it.
	 *
	 * Measured against the stored topics for the same reason `loose` is: a view
	 * hides capabilities without breaking them.
	 */
	const orphanCapabilities = createMemo<Capability[]>(() => {
		const known = topics.value();
		if (known.state !== "loaded") return [];
		const ids = new Set(known.value.map((topic) => topic.id));
		return (loadedValue(capabilities.value()) ?? []).filter(
			(capability) => !ids.has(capability.topic),
		);
	});

	const catalog = createMemo(() => loadedValue(tree.value()) ?? []);

	/**
	 * Narrows the tree as the reader types. Run against the loaded tree in the
	 * browser, so a keystroke costs nothing but a render; a view picked above
	 * narrows what is searched as well, which is what a reader looking at that
	 * view expects.
	 */
	const [query, setQuery] = createSignal("");
	const searching = () => query().trim() !== "";
	const found = createMemo(() => searchCatalog(catalog(), query()));

	/**
	 * Held here rather than inside the tree, because the fold-everything buttons
	 * belong in this screen's header: a long catalog is scrolled past, and a
	 * control buried at the top of the tree is gone by the time it is wanted.
	 *
	 * A search gets folding of its own: its results come up unfolded whatever
	 * the reader folded before, and clearing the search puts their folds back.
	 */
	const folding = useFolding(catalog);
	const foundFolding = useFolding(() => found().topics);
	const shownFolding = () => (searching() ? foundFolding : folding);

	/**
	 * The row to put back on screen. Saving reloads the tree, which unmounts the
	 * row that was written and leaves the page scrolled back to its top, so the
	 * row is remembered here until it can be found again.
	 */
	const [revealing, setRevealing] = createSignal<Anchor | undefined>(undefined);

	/** Points the URL at a row, which is also what makes it linkable. */
	const reveal = (anchor: Anchor) => {
		setRevealing(anchor);
		navigate(`${location.pathname}${location.search}${anchorHash(anchor)}`, {
			replace: true,
			scroll: false,
		});
	};

	/** A hash from outside — a pasted link, the back button — points as well. */
	createEffect(() => setRevealing(parseAnchor(location.hash)));

	/*
	 * Unfolding comes first: a row under a topic the reader folded is not
	 * rendered, so there would be nothing to scroll to. The lookup waits for the
	 * tree, and the scroll for the frame that puts the tree on screen. A row that
	 * is still not there keeps its claim, so the next load tries again.
	 */
	createEffect(() => {
		const anchor = revealing();
		const branches = loadedValue(tree.value());
		if (anchor === undefined || branches === undefined) return;

		const holder = anchorTopic(branches, anchor);
		if (holder !== undefined) untrack(() => shownFolding().expand(holder));
		requestAnimationFrame(() => {
			if (scrollToAnchor(anchor)) setRevealing(undefined);
		});
	});

	/**
	 * Files a question under one topic and no other. Membership lives on the
	 * topic, so moving a question means two writes: taking it out of whatever
	 * claimed it and putting it into the one that should.
	 */
	const assign = async (question: string, topic: string) => {
		const stored = loadedValue(topics.value()) ?? [];
		for (const candidate of stored) {
			const holds = candidate.questions.includes(question);
			if (candidate.id === topic) {
				if (!holds) {
					await api.patch("topics", candidate.id, {
						questions: [...candidate.questions, question],
					});
				}
			} else if (holds) {
				await api.patch("topics", candidate.id, {
					questions: candidate.questions.filter((entry) => entry !== question),
				});
			}
		}
	};

	const write = async <T extends { id: string }>(
		collection: Collection,
		entity: T,
		fresh: boolean,
	) => {
		setProblem(undefined);

		const result = fresh
			? await api.create(collection, entity as never)
			: await api.patch(collection, entity.id, entity as never);
		if (!result.ok) return setProblem(i18n.t().error.validation);

		// A fresh entity only learns its id from the response, which generated it.
		const id = result.value.id;
		setEditing({ kind: "none" });
		await reloadAll();
		return reveal({ kind: anchorKinds[collection], id });
	};

	/** A question and the topic it belongs to, written in that order. */
	const writeQuestion = async (entry: QuestionDraft, fresh: boolean) => {
		setProblem(undefined);
		if (entry.topic === "")
			return setProblem(i18n.t().catalog.questionTopicRequired);

		const result = fresh
			? await api.create("questions", entry.question)
			: await api.patch("questions", entry.question.id, entry.question);
		if (!result.ok) return setProblem(i18n.t().error.validation);

		// A fresh question only learns its id from the response, which generated it.
		const id = result.value.id;
		await assign(id, entry.topic);
		setEditing({ kind: "none" });
		await reloadAll();
		return reveal({ kind: "question", id });
	};

	/** Which topic currently lists a question, for the editor to start from. */
	const topicOf = (question: string): string =>
		(loadedValue(topics.value()) ?? []).find((topic) =>
			topic.questions.includes(question),
		)?.id ?? "";

	/** The stored topic behind a tree node, which carries the fields the tree drops. */
	const storedTopic = (id: string, title: string): Topic => {
		const known = topics.value();
		const found =
			known.state === "loaded"
				? known.value.find((candidate) => candidate.id === id)
				: undefined;
		return found ?? { ...blankTopic(), id, title };
	};

	/**
	 * The products the answer comparison compares, picked the way the tree
	 * endpoint picks the products it answers for: the view's products when it
	 * names any, every active product otherwise. Matching that rule is what keeps
	 * a product out of the dialog that the server was never going to answer for.
	 */
	const compared = createMemo<Product[]>(() => {
		const all = loadedValue(products.value()) ?? [];
		const view = (loadedValue(views.value()) ?? []).find(
			(candidate) => candidate.id === against(),
		);
		const wanted = view?.products ?? [];
		return wanted.length === 0
			? all.filter((product) => product.active)
			: all.filter((product) => wanted.includes(product.id));
	});

	const heading = (): string => {
		const state = editing();
		if (state.kind === "topic") return i18n.t().common.topic;
		if (state.kind === "capability") return i18n.t().common.capability;
		if (state.kind === "question") return i18n.t().common.question;
		if (state.kind === "answers") return i18n.t().catalog.compareAnswers;
		return "";
	};

	const subject = (): string => {
		const state = editing();
		if (state.kind === "topic") return state.topic.id;
		if (state.kind === "capability") return state.capability.id;
		if (state.kind === "question") return state.question.question.id;
		if (state.kind === "answers") return state.question.id;
		return "";
	};

	return (
		<>
			<Panel>
				<TextField
					label={i18n.t().catalog.search}
					placeholder={i18n.t().catalog.searchPlaceholder}
					value={query()}
					onInput={setQuery}
				/>
				{/*<SelectField
					label={i18n.t().coverage.view}
					hint={i18n.t().coverage.viewHint}
					placeholder={i18n.t().coverage.noView}
					choices={(loadedValue(views.value()) ?? []).map((view) => ({
						id: view.id,
						label: view.label,
					}))}
					value={against()}
					onPick={(view) => {
						setAgainst(view);
						tree.reload();
					}}
				/>*/}
			</Panel>

			<Panel
				title={i18n.t().catalog.title}
				actions={
					<>
						<IconButton
							label={i18n.t().tree.expandAll}
							icon={<UnfoldIcon />}
							disabled={
								shownFolding().openCount() === shownFolding().foldables().length
							}
							onClick={() => shownFolding().expandAll()}
						/>
						<IconButton
							label={i18n.t().tree.collapseAll}
							icon={<FoldIcon />}
							disabled={shownFolding().openCount() === 0}
							onClick={() => shownFolding().collapseAll()}
						/>
						{/*
              One decision, three shapes. Three side-by-side buttons read as
              three separate offers and crowd out the fold controls beside them.
            */}
						<MenuButton
							label={i18n.t().common.new}
							tone="primary"
							items={[
								{
									label: i18n.t().catalog.newTopic,
									onSelect: () =>
										open({ kind: "topic", topic: blankTopic(), fresh: true }),
								},
								{
									label: i18n.t().catalog.newCapability,
									onSelect: () =>
										open({
											kind: "capability",
											capability: blankCapability(),
											fresh: true,
										}),
								},
								{
									label: i18n.t().catalog.newQuestion,
									onSelect: () =>
										open({
											kind: "question",
											question: { question: blankQuestion(), topic: "" },
											fresh: true,
										}),
								},
							]}
						/>
					</>
				}
			>
				<Async value={tree.value()} onRetry={tree.reload}>
					{() => (
						<Show
							when={!searching() || found().topics.length > 0}
							fallback={<p class={styles.note}>{i18n.t().catalog.noMatches}</p>}
						>
							<TopicTree
								topics={found().topics}
								marks={found().marks}
								folding={shownFolding()}
								topicActions={(topic) => (
									<>
										<IconButton
											label={i18n.t().common.edit}
											icon={<PencilIcon />}
											onClick={() =>
												open({
													kind: "topic",
													topic: storedTopic(topic.id, topic.title),
													fresh: false,
												})
											}
										/>
										<DeleteButton
											collection="topics"
											id={topic.id}
											label={topic.title}
											onRemoved={reloadAll}
										/>
									</>
								)}
								capabilityActions={(capability, topic) => (
									<>
										<IconButton
											label={i18n.t().common.edit}
											icon={<PencilIcon />}
											onClick={() =>
												open({
													kind: "capability",
													capability: {
														id: capability.id,
														label: capability.label,
														description: capability.description ?? null,
														topic: topic.id,
														questions: capability.questions,
													},
													fresh: false,
												})
											}
										/>
										<DeleteButton
											collection="capabilities"
											id={capability.id}
											label={capability.label}
											onRemoved={reloadAll}
										/>
									</>
								)}
								questionActions={(question) => (
									<>
										<IconButton
											label={i18n.t().catalog.compareAnswers}
											icon={<CompareIcon />}
											onClick={() => open({ kind: "answers", question })}
										/>
										<IconButton
											label={i18n.t().common.edit}
											icon={<PencilIcon />}
											onClick={() =>
												open({
													kind: "question",
													question: {
														question: {
															id: question.id,
															title: question.title,
															body: question.body ?? null,
															answer_type:
																question.answer_type as unknown as AnswerType,
														},
														topic: topicOf(question.id),
													},
													fresh: false,
												})
											}
										/>
										<DeleteButton
											collection="questions"
											id={question.id}
											label={question.title}
											onRemoved={reloadAll}
										/>
									</>
								)}
							/>
						</Show>
					)}
				</Async>
			</Panel>

			{/*
        Outside the tree, so it cannot ride along inside it. Shown only when
        there is something wrong, because an empty warning trains the reader to
        stop looking.
      */}
			<Show when={orphanCapabilities().length > 0}>
				<Panel
					title={i18n.t().catalog.unassignedCapabilities}
					description={i18n.t().catalog.unassignedCapabilitiesHint}
				>
					<Table
						rows={orphanCapabilities()}
						rowKey={(capability) => capability.id}
						rowId={(capability) =>
							anchorId({ kind: "capability", id: capability.id })
						}
						columns={[
							{
								key: "label",
								header: i18n.t().common.label,
								cell: (capability) => capability.label || capability.id,
							},
							{
								key: "topic",
								header: i18n.t().catalog.danglingTopic,
								cell: (capability) => (
									<span class={styles.problem}>
										{capability.topic === ""
											? i18n.t().common.none
											: capability.topic}
									</span>
								),
							},
							{
								key: "questions",
								header: i18n.t().catalog.questions,
								cell: (capability) => capability.questions.length,
							},
							{
								key: "actions",
								header: i18n.t().common.actions,
								align: "end",
								cell: (capability) => (
									<span class={styles.rowActions}>
										<IconButton
											label={i18n.t().common.edit}
											icon={<PencilIcon />}
											onClick={() =>
												open({ kind: "capability", capability, fresh: false })
											}
										/>
										<DeleteButton
											collection="capabilities"
											id={capability.id}
											label={capability.label || capability.id}
											onRemoved={reloadAll}
										/>
									</span>
								),
							},
						]}
					/>
				</Panel>
			</Show>

			<Modal
				open={editing().kind !== "none"}
				title={heading()}
				description={subject()}
				size={editing().kind === "answers" ? "wide" : "regular"}
				onClose={close}
			>
				{/*
          Keyed, so picking a second entity of the same kind remounts the editor
          on the new one instead of leaving the first one's draft on screen.
        */}
				<Switch>
					<Match
						when={editing().kind === "topic" ? editing() : undefined}
						keyed
					>
						{(state) => {
							const entry = state as Extract<Editing, { kind: "topic" }>;
							return (
								<TopicEditor
									initial={entry.topic}
									catalog={catalog()}
									loose={loose()}
									problem={problem()}
									onSave={(topic) => void write("topics", topic, entry.fresh)}
									onCancel={close}
								/>
							);
						}}
					</Match>
					<Match
						when={editing().kind === "capability" ? editing() : undefined}
						keyed
					>
						{(state) => {
							const entry = state as Extract<Editing, { kind: "capability" }>;
							return (
								<CapabilityEditor
									initial={entry.capability}
									catalog={catalog()}
									loose={loose()}
									problem={problem()}
									onSave={(capability) =>
										void write("capabilities", capability, entry.fresh)
									}
									onCancel={close}
								/>
							);
						}}
					</Match>
					<Match
						when={editing().kind === "answers" ? editing() : undefined}
						keyed
					>
						{(state) => (
							<QuestionAnswers
								question={
									(state as Extract<Editing, { kind: "answers" }>).question
								}
								products={compared()}
							/>
						)}
					</Match>
					<Match
						when={editing().kind === "question" ? editing() : undefined}
						keyed
					>
						{(state) => {
							const entry = state as Extract<Editing, { kind: "question" }>;
							return (
								<QuestionEditor
									initial={entry.question}
									catalog={catalog()}
									loose={loose()}
									problem={problem()}
									onSave={(draft) => void writeQuestion(draft, entry.fresh)}
									onCancel={close}
								/>
							);
						}}
					</Match>
				</Switch>
			</Modal>
		</>
	);
};
